import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppEnvironment } from '../models/environment.model';

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  private _environments = signal<AppEnvironment[]>([]);
  private _selectedEnvironmentId = signal<string | null>(null);
  private _loading = signal(false);

  environments = this._environments.asReadonly();
  selectedEnvironmentId = this._selectedEnvironmentId.asReadonly();
  loading = this._loading.asReadonly();

  selectedEnvironment = computed(() => {
    const id = this._selectedEnvironmentId();
    return this._environments().find(e => e.id === id) || null;
  });

  constructor(private http: HttpClient) {}

  loadEnvironments(): void {
    this._loading.set(true);
    this.http.get<AppEnvironment[]>(`${environment.apiBaseUrl}/app-environments`).subscribe({
      next: (envs) => {
        this._environments.set(envs);
        if (envs.length > 0 && !this._selectedEnvironmentId()) {
          this._selectedEnvironmentId.set(envs[0].id);
        }
        this._loading.set(false);
      },
      error: () => this._loading.set(false),
    });
  }

  selectEnvironment(id: string): void {
    this._selectedEnvironmentId.set(id);
  }

  removeEnvironment(id: string): void {
    const remaining = this._environments().filter(e => e.id !== id);
    this._environments.set(remaining);
    if (this._selectedEnvironmentId() === id) {
      this._selectedEnvironmentId.set(remaining.length > 0 ? remaining[0].id : null);
    }
  }

  createEnvironment(name: string): Observable<AppEnvironment> {
    return this.http.post<AppEnvironment>(`${environment.apiBaseUrl}/app-environments`, { name }).pipe(
      tap((env) => {
        this._environments.update(envs => [...envs, env]);
        this._selectedEnvironmentId.set(env.id);
      })
    );
  }
}
